import React, { useEffect, useState, useRef } from "react";
import { TextField, Grid, Box, Button } from "@mui/material";
import { useAuth, useSnackbar, useLoading, useNavigation } from "../../../Context/Contexts";
import CenteredCard from "../../../components/Card/CenteredCardLayout";
import DefaultActions from "../../../components/Card/DefaultActions";
import { PrinterViewModel } from "../../../Types/ExportTypes";
import {
} from "../../../components/AutoComplete/AutoCompletes";
import { useTranslation } from "react-i18next";
import { ToolbarWithSaveHandle } from "../../../components/Toolbar/ToolbarWithSave";

const PrinterEditPage: React.FC<{ params?: { [key: string]: any } }> = ({ params }) => {
  const { id = null } = params || {};

  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const { showLoading, hideLoading } = useLoading();
  const { authService } = useAuth();
  const { showSnackbar } = useSnackbar();
  const { navigate, goBack } = useNavigation();
  const [viewModel, setViewModel] = useState<(PrinterViewModel) | null>(null);
  const toolbarRef = useRef<ToolbarWithSaveHandle>(null);

  const showHasChangesError = () => {
    toolbarRef.current?.showHasChangesError();
  };

  useEffect(() => {
    isLoading ? showLoading() : hideLoading();
  }, [isLoading, showLoading, hideLoading]);

  useEffect(() => {
    if (id) {
      setIsLoading(true);
      authService
        .get<PrinterViewModel>(`/settingsApi/GetPrinter?id=${id}`)
        .then((response) =>
          setViewModel(
            response
              ? { ...response, originalValues: { ...response }, hasChanges: false }
              : null
          )
        )
        .catch((error: any) => {
          showSnackbar(`${t("common.failedToFetchEntries")}: ${error.response?.data ?? error.message}`, "error");
        })
        .finally(() => setIsLoading(false));
    } else {
      const defaultPrinter: PrinterViewModel & { originalValues: Partial<PrinterViewModel>; hasChanges: boolean } = {
        createNew: true,
        originalValues: {},
        hasChanges: false,
      };
      defaultPrinter.originalValues = { ...defaultPrinter };
      setViewModel(defaultPrinter);
    }
  }, [id, authService]);

  const handleSave = async () => {
    if (!viewModel) return;
    if (!validateViewModel()) return;

    setIsLoading(true);
    try {
      const createNew = viewModel.createNew;
      const response = await authService.post<PrinterViewModel>("/settingsApi/CreateOrUpdatePrinter", viewModel);
      setViewModel(response ? { ...response, originalValues: { ...response }, hasChanges: false } : null);
      showSnackbar(t("dialog.savedSuccessfully"), "success");

      if (createNew === true) {
        navigate({ path: "/settings/printer/edit", params: { id: response.id } });
      }
    } catch {
      showSnackbar(t("dialog.saveErrorGeneric"), "error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (field: keyof PrinterViewModel, value: any) => {
    setViewModel((prev) => (prev ? { ...prev, [field]: value, hasChanges: true } : null));
  };

  const handleCancelChanges = () => {
    setViewModel((prev) => {
      if (!prev || !prev.originalValues) return prev;
      const original = prev.originalValues as PrinterViewModel;
      return { ...original, originalValues: { ...original }, hasChanges: false };
    });
  };

  const validateViewModel = () => {
    if (!viewModel) {
      showSnackbar(t("dialog.dataMissing"), "error");
      return false;
    }
    if (!viewModel.printerIdentity || viewModel.printerIdentity.trim() === "") {
      showSnackbar("Printer Identity is required.", "warning");
      return false;
    }

    return true;
  };

  const handleDelete = async () => {
    if (viewModel?.hasChanges) showHasChangesError();

    const isConfirmed = window.confirm(t("dialog.deleteEntityConfirm"));
    if (!isConfirmed) return;
    var newItem = viewModel?.createNew ?? false;
    if (newItem) {
      navigate({ path: "/settings/printer/list" });
      return;
    }

    setIsLoading(true);
    try {
      await authService.post(`/settingsApi/DeletePrinter`, viewModel);
      showSnackbar(t("dialog.deletedSuccessfully"), "success");;

      navigate({ path: "/settings/printer/list" });
    } catch (error: any) {
      showSnackbar(`${t("dialog.deleteFailed")} : ${error.response?.data ?? error.message}`, "error");

    } finally {
      setIsLoading(false);
    }
  };





  return (
    <>
      <CenteredCard
        ref={toolbarRef}
        title={t("common.edit") + " " + t("common.printer")}
        isLoading={isLoading}
        handleSave={handleSave}
        handleCancel={handleCancelChanges}
        hasChanges={viewModel?.hasChanges ?? false}
      >
        <Box sx={{ display: "flex", justifyContent: "flex-start", maxWidth: "fit-content", mb: 2 }}>
          <DefaultActions onSave={handleSave} onDelete={handleDelete} />
        </Box>
        {viewModel && (
          <Grid container spacing={2}>

            <Grid item xs={12}>
              <TextField
                label="Id"
                value={viewModel.id || ""}
                fullWidth
                margin="normal"
                disabled
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label={t("common.name")}
                value={viewModel.printerIdentity || ""}
                onChange={(e) => handleChange("printerIdentity", e.target.value)}
                fullWidth
                margin="normal"
                error={!viewModel.printerIdentity || viewModel.printerIdentity.trim() === ""}
                helperText={!viewModel.printerIdentity || viewModel.printerIdentity.trim() === "" ? t("dialog.nameRequired") : ""}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                label="Printnode Id"
                value={viewModel.printNodeId || ""}
                onChange={(e) => handleChange("printNodeId", e.target.value)}
                fullWidth
                type="number"
                margin="normal"
              />
            </Grid>



          </Grid>
        )}

        <Box sx={{ marginTop: 2, textAlign: "center" }}>
          <Button variant="contained" onClick={handleSave} disabled={isLoading || !viewModel?.hasChanges} sx={{ marginRight: 2 }}>
            {t("common.save")}
          </Button>
          <Button variant="outlined" onClick={handleCancelChanges} disabled={!viewModel?.hasChanges} sx={{ marginRight: 2 }}>
            {t("common.cancel")}
          </Button>
          <Button variant="outlined" onClick={goBack}>
            {t("common.back")}
          </Button>
        </Box>

      </CenteredCard>

    </>
  );
};

export default PrinterEditPage;
